import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Sparkles } from 'lucide-react';
import usePageTitle from '@/hooks/usePageTitle';
import { CURRENCIES } from '@/lib/format';

const COUNTRIES = [
  { code: 'IN', name: 'India',          currency: 'INR' },
  { code: 'US', name: 'United States',  currency: 'USD' },
  { code: 'AE', name: 'United Arab Emirates', currency: 'AED' },
  { code: 'GB', name: 'United Kingdom', currency: 'GBP' },
  { code: 'SG', name: 'Singapore',      currency: 'SGD' },
  { code: 'CA', name: 'Canada',         currency: 'CAD' },
  { code: 'AU', name: 'Australia',      currency: 'AUD' },
  { code: 'EU', name: 'European Union', currency: 'EUR' },
  { code: 'SA', name: 'Saudi Arabia',   currency: 'SAR' },
  { code: 'QA', name: 'Qatar',          currency: 'QAR' },
  { code: 'KW', name: 'Kuwait',         currency: 'KWD' },
  { code: 'OT', name: 'Other',          currency: 'USD' },
];

/**
 * OnboardingComplete, minimal one-step screen shown to new users to capture:
 *   username (unique), country, currency, display name (prefilled).
 * Always reachable post-OAuth or post-email-signup once their session lands.
 */
export default function OnboardingComplete() {
  usePageTitle('Welcome');
  const { user, profile, refreshProfile } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: '',
    username: '',
    country: 'IN',
    currency: 'INR',
  });
  const [busy, setBusy] = useState(false);
  const [usernameError, setUsernameError] = useState('');

  useEffect(() => {
    if (!profile && !user) return;
    setForm((f) => ({
      ...f,
      name: profile?.name || user?.user_metadata?.name || user?.user_metadata?.full_name || '',
      username: profile?.username || '',
      country: profile?.country || 'IN',
      currency: profile?.currency || 'INR',
    }));
  }, [profile, user]);

  // If user has already completed onboarding, skip to dashboard
  useEffect(() => {
    if (profile && profile.username && profile.country && profile.currency) {
      nav('/dashboard', { replace: true });
    }
  }, [profile, nav]);

  const handleCountry = (code) => {
    const c = COUNTRIES.find((x) => x.code === code) || COUNTRIES[0];
    setForm((f) => ({ ...f, country: code, currency: c.currency }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!user) return;
    if (!form.name.trim() || !form.username.trim()) {
      toast.error('Please add your name and a username.');
      return;
    }
    if (!/^[a-zA-Z0-9_.-]{3,24}$/.test(form.username.trim())) {
      setUsernameError('3–24 letters, numbers, _ . or -');
      return;
    }
    setBusy(true);
    setUsernameError('');
    const country = COUNTRIES.find((c) => c.code === form.country)?.name || 'India';
    const updatePayload = {
      name: form.name.trim(),
      full_name: form.name.trim(),
      username: form.username.trim().toLowerCase(),
      country,
      currency: form.currency,
    };

    // Safari + supabase-js sometimes throws "Body is disturbed or locked" when
    // the same client fires two queries in quick succession (the second one
    // tries to read a Response body the first one already consumed). We retry
    // the update once with a 250ms breather if we see that error. If the
    // retry also fails but we suspect the original write went through, we
    // still navigate forward, the destination /onboarding will refetch.
    const runUpdate = async () => {
      return await supabase.from('profiles').update(updatePayload).eq('id', user.id);
    };

    let result;
    try {
      result = await runUpdate();
    } catch (err) {
      const msg = String(err?.message || err || '');
      if (/disturbed|locked/i.test(msg)) {
        // Wait a beat, then retry once.
        await new Promise((r) => setTimeout(r, 350));
        try {
          result = await runUpdate();
        } catch (err2) {
          // Both attempts threw, but the first write may have succeeded.
          // Proceed optimistically rather than trap the user on this screen.
          console.warn('profile update threw twice (Body locked), proceeding optimistically:', err2?.message || err2);
          result = { error: null };
        }
      } else {
        setBusy(false);
        toast.error(msg || 'Could not save right now. Please try again.');
        return;
      }
    }

    const { error } = result || {};
    if (error) {
      setBusy(false);
      if (String(error.message || '').toLowerCase().includes('duplicate') || error.code === '23505') {
        setUsernameError('That username is taken. Try another.');
      } else if (/disturbed|locked/i.test(String(error.message || ''))) {
        // Treat as a soft-success, likely the write landed.
        console.warn('Soft-ignoring "Body is disturbed" on profile update.');
        toast.success('Welcome to 2120.');
        // Don't block on refreshProfile, let the next page refetch.
        refreshProfile().catch(() => {});
        nav('/onboarding', { replace: true });
      } else {
        toast.error(error.message || 'Could not save.');
      }
      return;
    }

    setBusy(false);
    toast.success('Welcome to 2120.');
    // Fire-and-forget refresh, don't await. Avoids the body-disturbed race
    // and the destination page (/onboarding) will refetch on mount anyway.
    refreshProfile().catch(() => {});
    nav('/onboarding', { replace: true });
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
      <Card className="w-full max-w-lg rounded-2xl border-border/70">
        <CardContent className="p-8 space-y-5">
          <div className="flex flex-col items-center gap-2 pb-3 border-b border-border/40">
            <div className="flex items-center gap-2.5">
              <img src="/brand/logo-mark.png" alt="2120" className="h-9 w-9" />
              <img src="/brand/wordmark-green.png" alt="2120" className="h-6 w-auto dark:hidden" />
              <img src="/brand/wordmark-gold.png" alt="2120" className="h-6 w-auto hidden dark:block" />
            </div>
            <div className="text-xs text-muted-foreground text-center">Financial Clarity For Modern Life.</div>
          </div>
          <div>
            <div className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground px-2 py-1 rounded-full bg-card border border-border/60">
              <Sparkles className="h-3 w-3 text-[hsl(var(--gold))]" /> 30-day Horizon trial included
            </div>
            <div className="font-display text-2xl font-semibold mt-2">A few last details</div>
            <p className="text-sm text-muted-foreground mt-1">So we can shape 2120 to your country and currency.</p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="full-name">Your name</Label>
              <Input
                id="full-name" required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="rounded-xl"
                data-testid="onboarding-name-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username" required
                value={form.username}
                onChange={(e) => { setForm({ ...form, username: e.target.value }); setUsernameError(''); }}
                placeholder="e.g. ashish"
                className="rounded-xl"
                data-testid="onboarding-username-input"
              />
              {usernameError ? (
                <div className="text-xs text-destructive">{usernameError}</div>
              ) : (
                <div className="text-xs text-muted-foreground">3&ndash;24 characters. Letters, numbers, _ . or -</div>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Country</Label>
                <Select value={form.country} onValueChange={handleCountry}>
                  <SelectTrigger className="rounded-xl" data-testid="onboarding-country-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c.code} value={c.code} data-testid={`country-${c.code}`}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Currency</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger className="rounded-xl" data-testid="onboarding-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(CURRENCIES || []).map((cur) => (
                      <SelectItem key={cur.code} value={cur.code}>{cur.symbol} {cur.code} , {cur.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button type="submit" className="w-full rounded-xl" disabled={busy} data-testid="onboarding-complete-submit">
              {busy ? 'Saving…' : 'Continue'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
